import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Crown, Sparkles, Star, Zap, XCircle, AlertTriangle } from "lucide-react";

interface SubscriptionTransitionProps {
  type: "upgrade" | "downgrade" | "expired";
  onComplete: () => void;
  duration?: number;
}

export const SubscriptionTransition = ({ 
  type, 
  onComplete, 
  duration = 3000 
}: SubscriptionTransitionProps) => {
  const [step, setStep] = useState(0);
  const [isComplete, setIsComplete] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (step < 3) {
        setStep(step + 1);
      } else {
        setIsComplete(true);
        setTimeout(onComplete, 500);
      }
    }, duration / 4);

    return () => clearTimeout(timer);
  }, [step, duration, onComplete]);

  if (type === "upgrade") {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm"
      >
        {/* Particle effects */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(20)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-2 h-2 rounded-full bg-yellow-400"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              animate={{
                y: [0, -100, -200],
                x: [0, (Math.random() - 0.5) * 100, (Math.random() - 0.5) * 200],
                scale: [0, 1, 0],
                opacity: [0, 1, 0],
              }}
              transition={{
                duration: 2,
                delay: Math.random() * 1,
                repeat: Infinity,
                repeatDelay: Math.random() * 2,
              }}
            />
          ))}
        </div>

        {/* Main content */}
        <motion.div
          initial={{ scale: 0.5, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: "spring", stiffness: 200 }}
          className="relative z-10 text-center"
        >
          {/* Crown icon */}
          <motion.div
            animate={{ 
              rotate: [0, -10, 10, -10, 0],
              scale: [1, 1.1, 1],
            }}
            transition={{ duration: 2, repeat: Infinity }}
            className="mx-auto mb-6 w-32 h-32 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full flex items-center justify-center shadow-2xl"
          >
            <Crown className="w-16 h-16 text-white" />
          </motion.div>

          {/* Text */}
          <AnimatePresence mode="wait">
            {step === 0 && (
              <motion.div
                key="step0"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="text-white"
              >
                <motion.h2 
                  animate={{ scale: [1, 1.05, 1] }}
                  transition={{ duration: 1, repeat: Infinity }}
                  className="text-3xl font-bold mb-2"
                >
                  Upgrading to Pro...
                </motion.h2>
                <p className="text-gray-300">Preparing your premium features</p>
              </motion.div>
            )}
            {step === 1 && (
              <motion.div
                key="step1"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="text-white"
              >
                <h2 className="text-3xl font-bold mb-2">Unlocking Features</h2>
                <div className="space-y-2 text-left text-gray-300">
                  <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.2 }}
                  >
                    <Zap className="inline w-4 h-4 mr-2" /> Custom bounty rewards
                  </motion.p>
                  <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.4 }}
                  >
                    <Sparkles className="inline w-4 h-4 mr-2" /> Priority listings
                  </motion.p>
                  <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.6 }}
                  >
                    <Star className="inline w-4 h-4 mr-2" /> Analytics dashboard
                  </motion.p>
                </div>
              </motion.div>
            )}
            {step === 2 && (
              <motion.div
                key="step2"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="text-white"
              >
                <h2 className="text-3xl font-bold mb-2">Almost there...</h2>
                <motion.div
                  className="w-64 h-2 bg-gray-700 rounded-full overflow-hidden mx-auto"
                >
                  <motion.div
                    className="h-full bg-gradient-to-r from-yellow-400 to-orange-500"
                    initial={{ width: "0%" }}
                    animate={{ width: "100%" }}
                    transition={{ duration: 0.8 }}
                  />
                </motion.div>
              </motion.div>
            )}
            {step === 3 && (
              <motion.div
                key="step3"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="text-white"
              >
                <motion.h2
                  animate={{ scale: [1, 1.1, 1] }}
                  transition={{ duration: 0.5, repeat: 2 }}
                  className="text-4xl font-bold mb-2 bg-gradient-to-r from-yellow-400 to-orange-500 bg-clip-text text-transparent"
                >
                  Welcome to Pro! 🎉
                </motion.h2>
                <p className="text-gray-300">All premium features are now active</p>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </motion.div>
    );
  }

  // Downgrade/Expired animations
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm"
    >
      {/* Particle effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(10)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 rounded-full bg-gray-500"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [0, 50, 100],
              opacity: [1, 0.5, 0],
            }}
            transition={{
              duration: 1.5,
              delay: Math.random() * 0.5,
            }}
          />
        ))}
      </div>

      {/* Main content */}
      <motion.div
        initial={{ scale: 0.5, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: "spring", stiffness: 200 }}
        className="relative z-10 text-center"
      >
        {/* Icon */}
        <motion.div
          animate={{ 
            scale: [1, 1.05, 1],
          }}
          transition={{ duration: 2, repeat: Infinity }}
          className="mx-auto mb-6 w-32 h-32 bg-gradient-to-br from-gray-600 to-gray-700 rounded-full flex items-center justify-center shadow-2xl"
        >
          {type === "expired" ? (
            <XCircle className="w-16 h-16 text-gray-300" />
          ) : (
            <AlertTriangle className="w-16 h-16 text-gray-300" />
          )}
        </motion.div>

        {/* Text */}
        <AnimatePresence mode="wait">
          {step === 0 && (
            <motion.div
              key="step0"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="text-white"
            >
              <motion.h2 
                animate={{ scale: [1, 1.02, 1] }}
                transition={{ duration: 1.5, repeat: Infinity }}
                className="text-3xl font-bold mb-2"
              >
                {type === "expired" ? "Subscription Expired" : "Downgrading to Free..."}
              </motion.h2>
              <p className="text-gray-300">
                {type === "expired" 
                  ? "Your Pro period has ended" 
                  : "Processing your subscription change"}
              </p>
            </motion.div>
          )}
          {step === 1 && (
            <motion.div
              key="step1"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="text-white"
            >
              <h2 className="text-3xl font-bold mb-2">Removing Features</h2>
              <div className="space-y-2 text-left text-gray-300">
                <motion.p
                  initial={{ opacity: 1 }}
                  animate={{ opacity: 0.5 }}
                  transition={{ delay: 0.2 }}
                  className="line-through"
                >
                  <Zap className="inline w-4 h-4 mr-2" /> Custom bounty rewards
                </motion.p>
                <motion.p
                  initial={{ opacity: 1 }}
                  animate={{ opacity: 0.5 }}
                  transition={{ delay: 0.4 }}
                  className="line-through"
                >
                  <Sparkles className="inline w-4 h-4 mr-2" /> Priority listings
                </motion.p>
                <motion.p
                  initial={{ opacity: 1 }}
                  animate={{ opacity: 0.5 }}
                  transition={{ delay: 0.6 }}
                  className="line-through"
                >
                  <Star className="inline w-4 h-4 mr-2" /> Analytics dashboard
                </motion.p>
              </div>
            </motion.div>
          )}
          {step === 2 && (
            <motion.div
              key="step2"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="text-white"
            >
              <h2 className="text-3xl font-bold mb-2">Almost done...</h2>
              <motion.div
                className="w-64 h-2 bg-gray-700 rounded-full overflow-hidden mx-auto"
              >
                <motion.div
                  className="h-full bg-gradient-to-r from-gray-400 to-gray-500"
                  initial={{ width: "0%" }}
                  animate={{ width: "100%" }}
                  transition={{ duration: 0.8 }}
                />
              </motion.div>
            </motion.div>
          )}
          {step === 3 && (
            <motion.div
              key="step3"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="text-white"
            >
              <motion.h2
                animate={{ scale: [1, 1.05, 1] }}
                transition={{ duration: 0.5, repeat: 2 }}
                className="text-4xl font-bold mb-2 text-gray-400"
              >
                Downgraded to Free
              </motion.h2>
              <p className="text-gray-300 mb-4">You can still enjoy our core features</p>
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
                className="text-sm text-gray-400"
              >
                Upgrade anytime to regain Pro access
              </motion.p>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </motion.div>
  );
};
